import { useMemo } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  HighlightType,
  HighlightVariant,
  Icon,
  type DataTableCellData,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'

export interface EarnMyAsset {
  currency: string
  portfolioId?: string
  productName?: string
  myEarnRate?: string
  customerEarnRate?: string
  availableFundsAmount?: string
  availableFundsLabel?: string
  comingSoon?: boolean
  onWithdrawClick?: () => void
  onDepositClick?: () => void
}

export interface EarnMyAssetsProps {
  assets: EarnMyAsset[]
  showCustomerEarnRate?: boolean
  title?: string
  availableColumnLabel?: string
  myEarnRateColumnLabel?: string
  depositOnly?: boolean
}

const ASSET_AVATAR_CURRENCY_OVERRIDES: Record<string, string> = {
  OUSD: 'USDC',
}

const getAssetAvatarCurrency = (currency: string) =>
  ASSET_AVATAR_CURRENCY_OVERRIDES[currency] ?? currency

const buildAssetActionsMenu = (
  asset: EarnMyAsset,
  t: TFunction<'translation', undefined>,
  onDetailsClick?: () => void
) => {
  const menuItems = [
    onDetailsClick && (
      <DropdownMenuItem key="details" LeadingIcon={<Icon name="Eye" />} onClick={onDetailsClick}>
        {t('earn.viewDetails')}
      </DropdownMenuItem>
    ),
    asset.onWithdrawClick && (
      <DropdownMenuItem
        key="withdraw"
        LeadingIcon={<Icon name="CoinArrowOutCustom" />}
        onClick={asset.onWithdrawClick}
      >
        {t('earn.withdraw')}
      </DropdownMenuItem>
    ),
    asset.onDepositClick && (
      <DropdownMenuItem
        key="deposit"
        LeadingIcon={<Icon name="CoinArrowInCustom" />}
        onClick={asset.onDepositClick}
      >
        {t('earn.deposit')}
      </DropdownMenuItem>
    ),
  ].filter((menuItem): menuItem is React.ReactElement => Boolean(menuItem))

  if (menuItems.length === 0) return []

  return [
    <DropdownMenu
      key={`actions-${asset.currency}`}
      DropdownMenuItems={menuItems}
      testId={`earn-asset-actions-${asset.currency}`}
    >
      <Button
        variant="ghost"
        iconElement={<Icon name="DotsThreeVerticalCustom" />}
        onClick={event => event.stopPropagation()}
      />
    </DropdownMenu>,
  ]
}

const buildDepositAction = (asset: EarnMyAsset, t: TFunction<'translation', undefined>) => {
  if (!asset.onDepositClick) return []

  return [
    <Button
      key={`deposit-${asset.currency}`}
      variant="ghost"
      noPadding
      iconElement={<Icon name="ArrowDown" />}
      onClick={event => {
        event.stopPropagation()
        asset.onDepositClick?.()
      }}
    >
      {t('earn.deposit')}
    </Button>,
  ]
}

export const EarnMyAssets = ({
  assets,
  showCustomerEarnRate = true,
  title,
  availableColumnLabel,
  myEarnRateColumnLabel,
  depositOnly = false,
}: EarnMyAssetsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns: DataTableHeaderProps[] = useMemo(() => {
    const allColumns: (DataTableHeaderProps | false)[] = [
      { key: 'asset', label: t('earn.assetColumn'), variant: DataTableCellVariants.TEXT },
      {
        key: 'productName',
        label: t('earn.productNameColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'myEarnRate',
        label:
          myEarnRateColumnLabel ??
          (showCustomerEarnRate ? t('earn.myEarnRateColumn') : t('earn.rateColumn')),
        variant: DataTableCellVariants.TEXT,
      },
      showCustomerEarnRate && {
        key: 'customerEarnRate',
        label: t('earn.customerEarnRateColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'availableFunds',
        label: availableColumnLabel ?? t('earn.availableFundsColumn'),
        variant: DataTableCellVariants.TEXT,
      },
      { key: 'actions', label: '', variant: DataTableCellVariants.ACTION },
    ]

    return allColumns.filter((column): column is DataTableHeaderProps => Boolean(column))
  }, [availableColumnLabel, myEarnRateColumnLabel, showCustomerEarnRate, t])

  const tableData: DataTableRowData[] = useMemo(
    () =>
      assets.map(asset => {
        const goToAssetDetails =
          depositOnly || asset.comingSoon || !asset.portfolioId
            ? undefined
            : () => navigate(`${PathNames.EARN}/${asset.portfolioId}`)

        const cells: (DataTableCellData | false)[] = [
          {
            key: 'asset',
            variant: DataTableCellVariants.TEXT,
            Avatar: (
              <Avatar type="currency" name={getAssetAvatarCurrency(asset.currency)} size="small" />
            ),
            Text: asset.currency,
          },
          asset.comingSoon
            ? {
                key: 'productName',
                variant: DataTableCellVariants.HIGHLIGHT,
                Highlights: [
                  <Highlight
                    key="coming-soon"
                    type={HighlightType.Feature}
                    variant={HighlightVariant.Emphasis}
                  >
                    {t('earn.comingSoon').toUpperCase()}
                  </Highlight>,
                ],
              }
            : {
                key: 'productName',
                variant: DataTableCellVariants.TEXT,
                Text: asset.productName,
              },
          {
            key: 'myEarnRate',
            variant: DataTableCellVariants.TEXT,
            Text: asset.myEarnRate ?? '',
          },
          showCustomerEarnRate && {
            key: 'customerEarnRate',
            variant: DataTableCellVariants.TEXT,
            Text: asset.customerEarnRate ?? '',
          },
          {
            key: 'availableFunds',
            variant: DataTableCellVariants.TEXT,
            Text: asset.availableFundsAmount ?? '',
            BottomText: depositOnly ? undefined : asset.availableFundsLabel,
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            align: DataTableColumnAlignments.RIGHT,
            Actions: depositOnly
              ? buildDepositAction(asset, t)
              : buildAssetActionsMenu(asset, t, goToAssetDetails),
          },
        ]

        return {
          id: asset.currency,
          onRowClick: goToAssetDetails,
          cells: cells.filter((cell): cell is DataTableCellData => Boolean(cell)),
        }
      }),
    [assets, depositOnly, showCustomerEarnRate, t, navigate]
  )

  return (
    <Box flexCol gap={16} testId="earn-my-assets">
      <h4 className="text-h4 text-foreground-prominent">{title ?? t('earn.myAssetsTitle')}</h4>

      <DataTable headers={columns} data={tableData} />
    </Box>
  )
}
